<template>
  <div class="slider-range-root">
    <div v-if="summaryText" class="slider-range-header">
      <span class="slider-range-summary">{{ summaryText }}</span>
    </div>

    <div class="slider-range-inputs">
      <div class="slider-range-field" v-if="only_from">
        <label class="slider-range-label m-0">{{ $t('search.from') }}</label>
        <div class="position-relative">
          <component
            :is="cleave"
            v-model="from"
            :raw="true"
            :options="options.number"
            @blur="blurActionCleave('show_options_from')"
            @input="changeInput()"
            :placeholder="$t('search.min')"
            class="form-control form-control-sm text-center"
            ref="cleave-component-0"
            id="cleave-component-0-range"
          ></component>
          <div class="container-floating-numbers-options" v-if="show_options_from">
            <div v-for="option in filteredOptions" :key="option" class="option-number" @click="selectModel(option, null)">
              {{ separatebycomma(option) }}
            </div>
          </div>
        </div>
      </div>

      <template v-else>
        <div class="slider-range-field">
          <label class="slider-range-label m-0">{{ $t('search.from') }}</label>
          <div class="position-relative">
            <component
              :is="cleave"
              v-model="from"
              :raw="false"
              :options="options.number"
              :placeholder="$t('search.min')"
              class="form-control form-control-sm text-center"
              @blur="blurActionCleave('show_options_from')"
              @input="changeInput()"
              ref="cleave-component-0"
              id="cleave-component-0-range"
            ></component>
            <div class="container-floating-numbers-options" v-if="show_options_from && show_options_from_and_to">
              <div v-for="option in filteredOptions" :key="option" class="option-number" @click="selectModel(option, 0)">
                {{ separatebycomma(option) }}
              </div>
            </div>
          </div>
        </div>
        <div class="slider-range-field">
          <label class="slider-range-label m-0">{{ $t('search.to') }}</label>
          <div class="position-relative">
            <component
              :is="cleave"
              v-model="to"
              :raw="false"
              :options="options.number"
              :placeholder="$t('search.max')"
              class="form-control form-control-sm text-center"
              @blur="blurActionCleave('show_options_to')"
              @input="changeInput()"
              ref="cleave-component-1"
              id="cleave-component-1-range"
            ></component>
            <div class="container-floating-numbers-options" v-if="show_options_to && show_options_from_and_to">
              <div v-for="option in filteredOptions" :key="option" class="option-number" @click="selectModel(option, 1)">
                {{ separatebycomma(option) }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <component
      :is="vue_slider"
      class="slider-range-slider"
      v-model="range"
      :direction="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
      :max="max_value"
      :min="min_value"
      :interval="interval"
      @drag-end="changeSlide"
      :tooltip="'none'"
    >
      <template v-slot:dot="{ value }">
        <div :class="getClasses(value)"></div>
      </template>

      <template v-slot:process="{ style, index }">
        <div class="vue-slider-process custom-class" :style="[style, getProgressStyle(style, index)]">
        </div>
      </template>
    </component>

    <div class="d-flex justify-content-between slider-range-bounds">
      <span>{{ separatebycomma(min_value) }}</span>
      <span>{{ separatebycomma(max_value) }}+</span>
    </div>

    <div class="slider-range-chips" @click.stop>
      <button type="button" class="slider-range-chip" @mousedown.prevent @click.stop="clearRange">{{ $t('search.clear') }}</button>
    </div>

    <small v-if="!only_from" class="d-block text-muted slider-range-hint">{{ $t('search.rangeHint') }}</small>
  </div>
</template>

<script>
import { BFormInput } from 'bootstrap-vue';
import { separatebycomma, trimNumber } from '@/libs/utils/formats';

export default {
  name: 'viewAvgSlider',
  props: {
    min_value: {
      type: Number,
      required: true
    },
    max_value: {
      type: Number,
      required: true
    },
    interval: {
      type: Number,
      required: true
    },
    init_range: {
      type: Array,
      required: false
    },
    slider: {
      type: Number,
      required: false,
    },
    identifier: {
      type: String,
      default: ''
    },
    only_from: {
      type: Boolean,
      default: false,
    },
    any_text: {
      type: String,
      default: ''
    },
    options_numbers: {
      type: Array,
      default: () => {
        return [10000, 50000, 100000, 500000, 1000000, 5000000, 10000000];
      }
    },
    show_options_from_and_to: {
      type: Boolean,
      default: true,
    }
  },
  components: {
    BFormInput,
  },
  data () {
    return {
      separatebycomma,
      cleave: null,
      vue_slider: null,
      range: null,
      from: null,
      to: null,
      show_options_from: false,
      show_options_to: false,
      options: {
        number: {
          numeral: true,
          numeralThousandsGroupStyle: 'thousand',
        }
      },
    }
  },
  computed: {
    anyLabel () {
      return this.any_text || this.$t('search.realty.any')
    },
    // Opciones sin el min ni el max (en esos valores el filtro no se envia)
    filteredOptions () {
      return this.options_numbers.filter(o => o > this.min_value && o < this.max_value)
    },
    summaryText () {
      if (this.only_from) {
        const v = typeof this.range === 'number' ? this.range : trimNumber(this.range)
        if (!v || v <= this.min_value) return this.anyLabel
        return `${this.$t('search.from')} ${separatebycomma(v)}`
      }
      if (!Array.isArray(this.range)) return this.anyLabel
      const lo = Number(this.range[0])
      const hi = Number(this.range[1])
      if (isNaN(lo) || isNaN(hi)) return this.anyLabel
      const openLow = lo <= this.min_value
      const openHigh = hi >= this.max_value
      if (openLow && openHigh) return this.anyLabel
      if (openHigh) return `${this.$t('search.from')} ${separatebycomma(lo)}`
      if (openLow) return `${this.$t('search.to')} ${separatebycomma(hi)}`
      return `${this.$t('search.between')} ${separatebycomma(lo)} ${this.$t('search.and')} ${separatebycomma(hi)}`
    }
  },
  async mounted() {
    this.vue_slider = (await import('vue-slider-component')).default;
    this.cleave = (await import('vue-cleave-component')).default;
  },
  created () {
    if (this.slider !== undefined) {
      this.range = this.slider
      this.syncInputsFromRange()
    } else if (this.init_range !== undefined) {
      this.range = this.init_range
      this.syncInputsFromRange()
    }
    setTimeout(() => {
      for (let index = 0; index < 2; index++) {
        if (this.$refs[`cleave-component-${index}`]) this.$refs[`cleave-component-${index}`].$el.addEventListener('focus', this.handleFocus);
      }
    }, 2000);
  },
  beforeDestroy() {
    for (let index = 0; index < 2; index++) {
      if (this.$refs[`cleave-component-${index}`]) this.$refs[`cleave-component-${index}`].$el.removeEventListener('focus', this.handleFocus);
    }
  },
  methods: {
    handleFocus(input_focus) {
      if (input_focus.target.id === 'cleave-component-0-range') {
        this.show_options_from = true;
      } else {
        this.show_options_to = true;
      }
    },
    parseValue(val, fallback) {
      if (val === null || val === undefined || val === '') return fallback
      const n = typeof val === 'number' ? val : trimNumber(val)
      return isNaN(n) ? fallback : n
    },
    // Deja vacios los inputs (placeholder) cuando estan en el limite, y limita el
    // rango a [min, max]. Asi, llegar al limite equivale a "vaciar" ese extremo
    // (el padre con getRange no lo envia).
    syncInputsFromRange() {
      if (this.only_from) {
        const v = Number(this.range)
        this.from = v <= this.min_value ? null : this.range
        return
      }
      const lo = Number(this.range[0])
      const hi = Number(this.range[1])
      this.from = lo <= this.min_value ? null : this.range[0]
      this.to = hi >= this.max_value ? null : this.range[1]
    },
    clampRange() {
      if (this.only_from) {
        let v = this.parseValue(this.from, this.min_value)
        if (v < this.min_value) v = this.min_value
        if (v > this.max_value) v = this.max_value
        this.range = v
        return
      }
      let from = this.parseValue(this.from, this.min_value)
      let to = this.parseValue(this.to, this.max_value)
      if (from < this.min_value) from = this.min_value
      if (to > this.max_value) to = this.max_value
      this.range = [from, to]
    },
    changeInput() {
      this.clampRange()
    },
    clearRange() {
      if (this.only_from) {
        this.range = this.min_value
        this.from = null
      } else {
        this.range = [this.min_value, this.max_value]
        this.from = null
        this.to = null
      }
      this.$emit('changeSlide', this.identifier, this.range)
    },
    blurActionCleave(model_options) {
      // Siempre emitir al salir del input para que dispare la consulta,
      // aunque la lista de valores sugeridos este desactivada.
      setTimeout(() => {
        this[model_options] = false;
        this.clampRange();
        this.syncInputsFromRange();
        this.$emit('changeSlide', this.identifier, this.range);
      }, 200);
    },
    changeSlide() {
      this.syncInputsFromRange();
      this.$emit('changeSlide', this.identifier, this.range)
    },
    getClasses(value) {
      if (value === this.min_value || value === this.max_value) return 'value-inactive'
      return 'value-active'
    },
    getProgressStyle(style) {
      if (this.range[0] !== this.min_value && this.range[1] !== this.max_value) {
        return {
          'left': style.left,
          'width': style.width,
          'background-color': '#7367f0'
        }
      } else if (this.range[0] === this.min_value && this.range[1] !== this.max_value) {
        const width = 100 - parseFloat(style.width)
        return {
          'left': style.width,
          'width': `${width}%`,
          'background-color': '#7367f0'
        }
      }

      const left = this.range[1] === this.max_value ? 0 : style.width;
      const width = this.range[0] === this.min_value ? style.width : style.left;
      return {
        left,
        width,
        'background-color': '#7367f0'
      }
    },
    blurActionInput(show_model) {
      setTimeout(() => {
        this[show_model] = false;
      }, 200);
    },
    selectModel(value, index) {
      if (index !== null) {
        const new_range = [...this.range]
        new_range[index] = value
        this.range = new_range
      } else {
        this.range = value
      }
      // En el limite (min/max) se vacia ese extremo (equivale a no enviarlo)
      this.syncInputsFromRange()
      this.$emit('changeSlide', this.identifier, this.range)
    }
  },
}
</script>
<style lang="scss" scoped>
.slider-range-root {
  white-space: normal;
  max-width: 100%;
}
.slider-range-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 8px;
  min-width: 0;
}
.slider-range-summary {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: #7367f0;
  text-align: right;
  white-space: normal;
  word-break: break-word;
}
.slider-range-inputs {
  display: flex;
  gap: 10px;
}
.slider-range-field {
  flex: 1 1 0;
  min-width: 0;
}
.slider-range-label {
  font-size: 11px;
  color: #b9b9c3;
  text-align: left;
}
.slider-range-inputs .form-control {
  width: 100%;
  height: 34px;
}
.slider-range-slider {
  margin-top: 2px;
  margin-bottom: 0;
}
.slider-range-slider ::v-deep .vue-slider {
  margin: 0;
}
.slider-range-bounds {
  font-size: 11px;
  color: #b9b9c3;
  margin-top: 2px;
}
.slider-range-hint {
  display: block;
  font-size: 11px;
  margin-top: 10px;
  white-space: normal;
}
.slider-range-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.slider-range-chip {
  border: 1px solid #d8d6de;
  background: transparent;
  color: #6e6b7b;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
  transition: all 0.15s ease;
}
.slider-range-chip:hover {
  border-color: #7367f0;
  color: #7367f0;
}
.container-floating-numbers-options {
  background-color: white;
  position: absolute;
  z-index: 5;
  width: 100%;
  left: 0;
  box-shadow: 0 5px 25px rgba(34, 41, 47, 0.2);
  font-family: 'avenir-medium';

  .option-number {
    padding: 0.6em 1em;
    cursor: pointer;

    &:hover {
      background-color: #EFF3F6;
    }
  }
}
</style>
